import { IdName } from 'src/app/shared/models/id-name';
import { INextOfKin } from './next-of-kin.model';
import { IAttachment } from '../../attachments/models/attachments.model';

export interface IUserSettingsDetails {
  enableDueListNotifications: boolean;
}

export interface IUsersExportFilters {
  includeDeleted?: boolean;
  includePaused?: boolean;
  includeDeactivated?: boolean;
}

export interface IUser {
  id: string;
  tenantId: string;
  firstName: string;
  middleName: string;
  lastName: string;
  isActive: boolean;
  employedAs: string;
  settings: IUserSettingsDetails;
}

export interface IUserRole {
  roleId: string;
  issuedForDepartments: IdName[];
}

export interface IUserTableRole extends IdName {
  issuedForDepartments: IdName[];
}

export interface IUserTable extends IUser {
  loginEmail: string;
  hasDeletedEmployment: boolean;
  isEmailConfirmed: boolean;
  accessToDepartments: string[];
  employmentDepartment: string;
  roles: IUserTableRole[];
}

export interface IUserTenantInfo {
  employmentDepartment: IdName;
  accessToDepartments: IdName[];
  employmentAgreement: string;
  contractFrom: string;
  contractTo: string;
  position: IdName;
  shift: IdName;
  tenant: IdName;
  deletedOrPausedDate?: Date;
  deleted: boolean;
  isPaused: boolean;
  userEmploymentNumber: string;
  roles: IUserRole[];
  moduleAdmins: string[];
}

export enum ExistingEmailUserUpdateOption {
  CreateWithNoConfirmation = 'CreateWithNoConfirmation',
  SendConfirmationEmail = 'SendConfirmationEmail',
}

export enum UpdateUserCommandStatus {
  Updated = 'Updated',
  ExistingEmailUserFound = 'ExistingEmailUserFound',
  EmailUpdateDecisionRequired = 'EmailUpdateDecisionRequired',
}

export enum EmailChangeRequestProgressStep {
  WaitingForOldEmailApproval = 'WaitingForOldEmailApproval',
  WaitingForNewEmailConfirmation = 'WaitingForNewEmailConfirmation',
  Completed = 'Completed',
  Failed = 'Failed',
}

export enum EmailChangeRequestErrorCode {
  None = 'None',
  SameEmail = 'SameEmail',
  EmailAlreadyTaken = 'EmailAlreadyTaken',
  EmailConfirmationServiceDisabled = 'EmailConfirmationServiceDisabled',
  EmailSendingFailed = 'EmailSendingFailed',
  ApiSyncFailed = 'ApiSyncFailed',
  IdentityUpdateFailed = 'IdentityUpdateFailed',
}

export interface IEmailChangeProgress {
  currentLoginEmail?: string | null;
  requestedLoginEmail?: string | null;
  progressStep?: EmailChangeRequestProgressStep | null;
  errorCode?: EmailChangeRequestErrorCode | null;
}

export interface IEmailChangeProgressContainer {
  emailChangeProgress?: IEmailChangeProgress | null;
}

export interface IExistingEmailUserEmployment {
  employmentId: string;
  employmentVersionId: string;
  tenant: IdName;
  isRequestedTenant: boolean;
  position?: IdName | null;
  employmentDepartment?: IdName | null;
  accessToDepartments: IdName[];
  shift: IdName;
  contractFrom: string;
  contractTo?: string | null;
  employmentAgreement: string | number;
  userEmploymentNumber?: string | null;
  deleted: boolean;
  isPaused: boolean;
  deletedOrPausedDate?: string | null;
}

export interface IExistingEmailUser {
  id: string;
  oid: string;
  loginEmail: string;
  firstName?: string | null;
  middleName?: string | null;
  lastName?: string | null;
  fullName?: string | null;
  isActive: boolean;
  deleted: boolean;
  isProductOwner: boolean;
  employments: IExistingEmailUserEmployment[];
}

export interface IEmailUpdateDecision {
  userId: string;
  currentLoginEmail: string;
  requestedLoginEmail: string;
  isEmailConfirmed: boolean;
  availableOptions: ExistingEmailUserUpdateOption[];
}

export interface IUpdateUserResult {
  status: UpdateUserCommandStatus;
  existingUser?: IExistingEmailUser | null;
  emailUpdateDecision?: IEmailUpdateDecision | null;
  availableOptions: ExistingEmailUserUpdateOption[];
}

export interface IUserProductOwnerEmployment {
  tenantId: string;
  tenantName: string;
  departmentNames: string[];
  updated?: string | Date | null;
  deleted: boolean;
  isPaused: boolean;
}

export interface IUserProductOwnerProfile {
  id: string;
  tenantId: string;
  tenantName: string;
  firstName: string;
  middleName?: string | null;
  lastName: string;
  fullName: string;
  employmentData: IUserProductOwnerEmployment[];
}

export interface IAccountAccessDetails {
  loginEmail: string;
  lockoutEnd?: string | Date | null;
  accessFailedCount: number;
  isPasswordSet: boolean;
}

export interface IUserProductOwnerView {
  id: string;
  loginEmail: string;
  userProfileData: IUserProductOwnerProfile[];
  isActive: boolean;
  deleted: boolean;
  isProductOwner: boolean;
  userUpdatedDate?: string | Date | null;
  accountAccessDetails?: IAccountAccessDetails | null;
}

// tempporary model for mocks, remove when merging to dev
export interface IUserCompactDetails extends IUser {
  dayOfBirth: string;
}

export interface IUserDetails extends IUser, IEmailChangeProgressContainer {
  gender: string;
  isActive: boolean;
  loginEmail: string;
  mobilePrefix: string;
  mobile: string;
  address: string;
  zip: string;
  city: string;
  country: string;
  nationality: string;
  placeOfBirth: string;
  nearestAirport: string;
  dateOfEmployment?: string | null;
  socialSecurityNumber: string;
  dayOfBirth?: string | null;
  bankAccountNumber: string;
  ibanNumber: string;
  swiftNumber: string;
  passportNumber: string;
  passportIssuingState: string;
  passportExpirationDate?: string | null;
  seamansBookNumber: string;
  seamansBookIssuingState: string;
  seamansBookExpirationDate?: string | null;
  nextOfKins: INextOfKin[];
  deleted: boolean;
  settings: IUserSettingsDetails;
  isEmailConfirmed?: boolean;
  clothingSize: string | null;
  shoeSize: string | null;
  visaNo: string | null;
  userEmploymentNumber: string;
  profilePicture?: IAttachment;
  isProfilePictureRemoved: boolean;
  signature?: any;
  isSignatureRemoved: boolean;
  roles: IUserRole[];
}

export interface IUserRequest extends IUserDetails {
  departmentIds: string[];
  departmentGroupIds: string[];
  jobPositionId: string;
  shiftId: string;
  employmentAgreement: string;
  contractFrom: string;
  contractTo: string;
  settings: IUserSettingsDetails;
  moduleAdmins: string[];
  emailUpdateOption?: ExistingEmailUserUpdateOption;
}

export interface IUserModel extends IUserDetails {
  belongsToTenants: IUserTenantInfo[];
}

export interface IUserSearchResult {
  email: string;
  fullName: string;
  id: string;
}

export function map(userDetails: IUserModel, tenantId: string): IUserTable {
  return <IUserTable>{
    id: userDetails?.id,
    tenantId: userDetails?.tenantId,
    firstName: userDetails?.firstName,
    middleName: userDetails?.middleName,
    lastName: userDetails?.lastName,
    isActive: userDetails?.isActive,
    employedAs: userDetails?.belongsToTenants?.find((ut) => ut.tenant.id === tenantId)?.position
      .name,
  };
}

export interface IUserListResult {
  firstName: string;
  middleName: string;
  lastName: string;
  loginEmail: string;
  id: string;
}

export interface IUserFilterListItem {
  id: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  departments?: IdName[];
}

export interface IUserHistoryUser {
  id: string;
  oid: string;
  firstName?: string | null;
  middleName?: string | null;
  lastName?: string | null;
  fullName?: string | null;
}

export interface IUserHistoryChange {
  field: string;
  oldValues: Array<string | null>;
  newValues: Array<string | null>;
}

export interface IUserHistoryResult {
  id: string;
  versionId: string;
  created: string;
  updated?: string | null;
  createdBy: IUserHistoryUser;
  updatedBy: IUserHistoryUser;
  action: string;
  actionDescription?: string;
  subjectName?: string | null;
  changes: IUserHistoryChange[];
}
